import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Grid,
    CircularProgress
} from "@mui/material";
import Header from "../../../../common/Header";
import Footer from "../../../../common/Footer";
import { apiList, invokeApi } from "../../../../services/apiServices";
import { config } from "../../../../config/config";

const MaterialsCategory = () => {
    const { category, subcategory } = useParams(); // Added subcategory
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [displayCategoryName, setDisplayCategoryName] = useState("");

    const normalize = (str) => str?.toLowerCase().replace(/ /g, '-') || "";

    useEffect(() => {
        const fetchMaterials = async () => {
            try {
                // Fetch all PDFs and filter by category
                // Ideally backend supports filtering, but for now filtering frontend side based on previous pattern
                const response = await invokeApi(config.getMyCollege + apiList.getPdfPublications, {});
                if (response?.status === 200 && response?.data?.pdfs) {
                    const filtered = response.data.pdfs.filter(
                        pdf => {
                            const dbCategory = normalize(pdf.categoryName || pdf.category || pdf.category_name);
                            const categoryMatch = dbCategory === category;

                            let subcategoryMatch = true;
                            if (subcategory) {
                                const dbSubcategory = normalize(pdf.subcategory || pdf.subcategoryName);
                                if (subcategory === "other") {
                                    // Match items with no subcategory or explicitly 'other'
                                    subcategoryMatch = !dbSubcategory || dbSubcategory === "other";
                                } else {
                                    subcategoryMatch = dbSubcategory === subcategory;
                                }
                                console.log(`Checking PDF: ${pdf.title}, Category: ${dbCategory} vs ${category}, Sub: ${dbSubcategory} vs ${subcategory}, Match: ${categoryMatch && subcategoryMatch}`);
                            }
                            return categoryMatch && subcategoryMatch;
                        }
                    );
                    setMaterials(filtered);
                    if (filtered.length > 0) {
                        // Use category from first item, and append subcategory if present
                        let title = filtered[0].categoryName || filtered[0].category || filtered[0].category_name;
                        if (subcategory && subcategory !== "other") {
                            // Try to find the original subcategory name from the item
                            const subName = filtered[0].subcategory || filtered[0].subcategoryName;
                            if (subName) {
                                title += ` - ${subName}`;
                            } else {
                                // Fallback to capitalizing the slug
                                title += ` - ${subcategory.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`;
                            }
                        } else if (subcategory === "other") {
                            title += " - General";
                        }
                        setDisplayCategoryName(title);
                    } else {
                        // Fallback title when no items found (though ideally we found items)
                        let title = category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        if (subcategory) {
                            title += ` - ${subcategory.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
                        }
                        setDisplayCategoryName(title);
                    }
                }
            } catch (error) {
                console.error("Error fetching materials:", error);
            } finally {
                setLoading(false);
            }
        };

        if (category) {
            fetchMaterials();
        }
    }, [category, subcategory]); // Added subcategory to dependency array

    return (
        <>
            <Header />
            <Box sx={{ minHeight: "60vh", py: 5, backgroundColor: "#f9f9f9" }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        align="center"
                        gutterBottom
                        sx={{
                            color: "#005555",
                            fontWeight: 400,
                            mb: 5
                        }}
                    >
                        {displayCategoryName}
                    </Typography>

                    <Box sx={{ width: "60px", height: "4px", backgroundColor: "#d3d3d3", mx: "auto", mb: 6 }} />

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : materials.length > 0 ? (
                        <Grid container spacing={4} justifyContent="center">
                            {materials.map((item) => (
                                <Grid item key={item.id} xs={12} sm={6} md={4}>
                                    <Box
                                        onClick={() => window.open(item.pdfUrl, "_blank")}
                                        sx={{
                                            cursor: "pointer",
                                            textAlign: "center",
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                            "&:hover": {
                                                transform: "translateY(-8px)",
                                                "& .MuiTypography-root": {
                                                    color: "#005555"
                                                }
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: "100%",
                                                aspectRatio: "1/1", // Shorter height
                                                mb: 2,
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                                boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                                                border: "1px solid #eee",
                                                backgroundColor: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={item.imageUrl}
                                                alt={item.title}
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover" // Fills the space perfectly
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontSize: "0.95rem", // Slightly smaller for attractiveness
                                                fontWeight: 600,
                                                lineHeight: 1.2,
                                                px: 1,
                                                color: "#333",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                minHeight: "2.4em"
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "0.75rem",
                                                fontWeight: 500,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                color: "#00796b",
                                                mt: 1
                                            }}
                                        >
                                            View Report
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Typography align="center" variant="h6" color="textSecondary">
                            No materials found in this category.
                        </Typography>
                    )}
                </Container>
            </Box>
            <Footer variant="light" />
        </>
    );
};

export default MaterialsCategory;
